import { type CSSProperties, type ReactNode } from "react";

import "./BTBottomSheet.less";

import { BTDrawer, type IBTDrawerProps } from "../BTDrawer/BTDrawer";

export interface IBTBottomSheetProps
    extends Pick<
        IBTDrawerProps,
        "visible" | "onClose" | "destroyOnClose" | "zIndex" | "keyboard" | "role"
    > {
    children: ReactNode;
    /** Optional class name for the bottom sheet */
    className?: string;
}

const contentWrapperStyle: CSSProperties = {
    height: "auto",
    maxHeight: "85vh",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: "hidden",
};

const drawerStyle: CSSProperties = {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
};

const bodyStyle: CSSProperties = {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
};

/**
 * A mobile-optimized bottom sheet component that slides up from the bottom.
 * Wraps BTDrawer with iOS-style bottom sheet styling including:
 * - Rounded top corners
 * - Auto height with max 85vh
 * - Drag handle indicator
 */
export function BTBottomSheet({
    children,
    visible,
    onClose,
    className,
    destroyOnClose = true,
    role = "dialog",
    ...otherProps
}: IBTBottomSheetProps) {
    return (
        <BTDrawer
            visible={visible}
            onClose={onClose}
            placement="bottom"
            closable={false}
            className={`bt-bottom-sheet ${className ?? ""}`}
            destroyOnClose={destroyOnClose}
            contentWrapperStyle={contentWrapperStyle}
            drawerStyle={drawerStyle}
            bodyStyle={bodyStyle}
            role={role}
            {...otherProps}
        >
            <div
                className="bt-bottom-sheet-handle"
                role="button"
                aria-label="Drag handle"
                data-testid="bt-bottom-sheet-handle"
            />
            <div className="bt-bottom-sheet-body" data-testid="bt-bottom-sheet-body">
                {children}
            </div>
        </BTDrawer>
    );
}
